import type {
  OnboardingAnswers,
  OnboardingData,
  OnboardingInput
} from '~/types/onboarding';
import { DEFAULT_ONBOARDING_ANSWERS } from '~/types/onboarding';
import type { CustomerOnboardingInput } from '~~/lib/Shop/generated/schema';

export function parseApplicationIds(
  value: string | null | undefined
): string[] {
  if (!value) {
    return [];
  }

  return value
    .split(',')
    .map((id: string) => id.trim())
    .filter((id: string) => id.length > 0);
}

export function mapDataToAnswers(
  data: OnboardingData | null
): OnboardingAnswers {
  if (!data) {
    return { ...DEFAULT_ONBOARDING_ANSWERS };
  }

  return {
    industryId: data.cp_industry_id ?? null,
    companySizeId: data.cp_company_size ?? null,
    customerGroupId: data.cp_customer_group_id ?? null,
    jobPositionId: data.cp_job_position_id ?? null,
    buyingJourneyPhase: data.cp_buying_journey_phase ?? null,
    applicationIds: parseApplicationIds(data.cp_application_id)
  };
}

export function mapAnswersToInput(
  answers: OnboardingAnswers,
  status?: OnboardingInput['cp_onboarding_status']
): OnboardingInput {
  const input: OnboardingInput = {
    cp_industry_id: answers.industryId,
    cp_company_size: answers.companySizeId,
    cp_customer_group_id: answers.customerGroupId,
    cp_job_position_id: answers.jobPositionId,
    cp_buying_journey_phase: answers.buyingJourneyPhase,
    cp_application_id:
      answers.applicationIds.length > 0
        ? answers.applicationIds.join(',')
        : null
  };

  if (status !== undefined) {
    input.cp_onboarding_status = status;
  }

  return input;
}

/** Applies a partial Magento patch onto the current UI answers. */
export function mergeOnboardingInputIntoAnswers(
  current: OnboardingAnswers,
  input: OnboardingInput
): OnboardingAnswers {
  const next: OnboardingAnswers = { ...current };

  if (input.cp_industry_id !== undefined) {
    next.industryId = input.cp_industry_id;
  }

  if (input.cp_company_size !== undefined) {
    next.companySizeId = input.cp_company_size;
  }

  if (input.cp_customer_group_id !== undefined) {
    next.customerGroupId = input.cp_customer_group_id;
  }

  if (input.cp_job_position_id !== undefined) {
    next.jobPositionId = input.cp_job_position_id;
  }

  if (input.cp_buying_journey_phase !== undefined) {
    next.buyingJourneyPhase = input.cp_buying_journey_phase;
  }

  if (input.cp_application_id !== undefined) {
    next.applicationIds = parseApplicationIds(input.cp_application_id);
  }

  return next;
}

/** Maps the webapp onboarding payload to the Magento GraphQL input shape. */
export function mapOnboardingInputToCustomerOnboardingInput(
  input: OnboardingInput
): CustomerOnboardingInput {
  return {
    cp_onboarding_status: input.cp_onboarding_status,
    cp_industry_id: input.cp_industry_id,
    cp_company_size: input.cp_company_size,
    cp_customer_group_id: input.cp_customer_group_id,
    cp_job_position_id: input.cp_job_position_id,
    cp_country_code: input.cp_country_code,
    cp_application_id: input.cp_application_id,
    cp_buying_journey_phase: input.cp_buying_journey_phase
  };
}
